import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { Alert, Box, Button, MenuItem, Select, Stack, Typography } from '@mui/material';
import { getLiveCoach, setLiveCoachMode, sendLiveCoachFeedback } from '../../api/communicationApi';
import T from '../common/T';

const speakerLabel = (speaker) => ({ employee: 'Mitarbeiter', customer: 'Kunde', automated_system: 'Automatische Ansage' }[speaker] || 'Sprecher unklar');
const segmentTime = (segment) => {
  const value = segment.received_at;
  if (!value) return '';
  const date = new Date(/Z$|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString('de-CH', { timeZone: 'Europe/Zurich' });
};
const uncertain = (segment) => segment.confidence == null || !Number.isFinite(Number(segment.confidence)) || Number(segment.confidence) < 0.7;

export default function LiveCallCoach({ conversationId, callSid }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const revision = useRef(-1);
  const transcript = useRef(null);
  const [follow, setFollow] = useState(true);
  useEffect(() => {
    setState(null); revision.current = -1; setError('');
    if (!conversationId || !callSid) return undefined;
    const controller = new AbortController();
    let timer;
    const poll = async () => {
      try {
        const data = await getLiveCoach(conversationId, callSid, revision.current, controller.signal);
        if (!controller.signal.aborted) {
          setError('');
          if (data.changed !== false) { setState(data); revision.current = data.revision ?? -1; }
        }
      } catch (e) {
        if (!controller.signal.aborted) setError('Live-Coach momentan nicht erreichbar. Das Telefonat läuft weiter.');
      } finally { if (!controller.signal.aborted) timer = window.setTimeout(poll, 1500); }
    };
    poll();
    const clock = window.setInterval(() => setNow(Date.now()), 1000);
    return () => { controller.abort(); window.clearTimeout(timer); window.clearInterval(clock); };
  }, [conversationId, callSid]);
  useEffect(() => { if (follow && transcript.current) transcript.current.scrollTop = transcript.current.scrollHeight; }, [state, follow]);
  const mutate = async (fn) => {
    setBusy(true);
    try { await fn(); revision.current = -1; setState((old) => old ? { ...old, suggestion: null } : old); }
    catch (e) { setError(apiErrorMessage(e, 'Änderung konnte nicht gespeichert werden.')); }
    finally { setBusy(false); }
  };
  if (!conversationId || !callSid) return null;
  const suggestion = state?.suggestion && new Date(state.suggestion.valid_until).getTime() > now ? state.suggestion : null;
  return <Box sx={{ p: 2, bgcolor: 'white', color: '#1e293b', textAlign: 'left', width: '100%', borderTop: '2px solid #32cd32' }}>
    <Typography fontWeight={700}><T>Live-Transkript und Gesprächscoach</T></Typography>
    {error && <Alert severity="warning"><T>{error}</T></Alert>}
    {!state?.available && <Typography variant="body2">{state?.enabled === false ? 'Live-Coaching ist noch nicht freigeschaltet.' : 'Warte auf das Live-Transkript …'}</Typography>}
    {state?.available && <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 1 }}>
        <Typography component="label" htmlFor="live-coach-mode"><T>Gesprächszweck</T></Typography>
        <Select size="small" inputProps={{ id: 'live-coach-mode' }} value={state.mode} disabled={busy} onChange={(e) => mutate(() => setLiveCoachMode(state.id, e.target.value))}>
          <MenuItem value="unknown"><T>Bitte wählen</T></MenuItem><MenuItem value="sales"><T>Sales</T></MenuItem><MenuItem value="support"><T>Kundendienst</T></MenuItem>
        </Select>
        <Typography variant="caption">{state.direction?.toLowerCase() === 'inbound' ? 'Eingehend' : state.direction?.toLowerCase() === 'outbound' ? 'Ausgehend' : 'Richtung unklar'} · {state.status}</Typography>
      </Stack>
      <Box ref={transcript} role="log" aria-label="Gesprächsverlauf" tabIndex={0} onScroll={() => { const e = transcript.current; setFollow(e.scrollHeight - e.scrollTop - e.clientHeight < 40); }} sx={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e2e8f0', p: 1 }}>
        {(state.segments || []).map((s) => <Box key={s.id} sx={{ my: 0.8 }}><Typography variant="caption" color="text.secondary"><T>Empfangen </T>{segmentTime(s)}{uncertain(s) ? ' · Erkennung unsicher' : ''}</Typography><Typography variant="body2"><strong>{speakerLabel(s.speaker)}:</strong> {s.text}</Typography></Box>)}
        {(state.partials || []).map((s) => <Typography key={s.track} color="text.secondary" variant="body2">{speakerLabel(s.speaker)}: {s.text}<T> … (vorläufig)</T></Typography>)}
      </Box>
      {!follow && <Button onClick={() => setFollow(true)}><T>Zum aktuellen Gespräch</T></Button>}
      {state.status === 'limit_reached' && <Alert severity="info"><T>Live-Anzeige hat die Sitzungsgrenze erreicht. Das bestehende Gesprächsprotokoll bleibt separat erhalten.</T></Alert>}
      {suggestion && <Alert severity="success" sx={{ mt: 1 }}>
        <Typography fontWeight={700}>{suggestion.phrase}</Typography><Typography variant="body2">{suggestion.reason}</Typography>
        <Typography variant="caption">{suggestion.title}<T> · Wissensstand </T>{suggestion.knowledge_version}{suggestion.source_page ? ` · PDF-Seite ${suggestion.source_page}` : ''}</Typography>
        <Stack direction="row"><Button disabled={busy} onClick={() => mutate(() => sendLiveCoachFeedback(state.id, suggestion.knowledge_id, 'used'))}><T>Verwendet</T></Button><Button disabled={busy} onClick={() => mutate(() => sendLiveCoachFeedback(state.id, suggestion.knowledge_id, 'dismissed'))}><T>Ausblenden</T></Button><Button disabled={busy} onClick={() => mutate(() => sendLiveCoachFeedback(state.id, suggestion.knowledge_id, 'unhelpful'))}><T>Nicht hilfreich</T></Button></Stack>
      </Alert>}
      <Typography variant="caption"><T>Antwortvorschläge aus der Wissensdatenbank. Du entscheidest und sprichst. Keine automatische Antwort an Kunden.</T></Typography>
    </>}
  </Box>;
}
LiveCallCoach.propTypes = { conversationId: PropTypes.string, callSid: PropTypes.string };
